import React, { useState, useEffect } from 'react';
import { 
  Drawer, Container, Typography, FormControlLabel, Checkbox, Slider, 
  Divider, Box, Button, TextField, Chip, Autocomplete, useTheme
} from '@mui/material';

const FilterDrawer = ({ open, onClose, filters, setFilters, allTags }) => {
  const [tagInput, setTagInput] = useState('');
  const [excludeTagInput, setExcludeTagInput] = useState('');
  const [knownTags, setKnownTags] = useState([]);
  const theme = useTheme();

  useEffect(() => {
    if (allTags && allTags.length > 0) {
      setKnownTags(allTags);
    }
  }, [allTags]);

  useEffect(() => {
    // Add 'NSFW' to excludeTags by default if it's not already there
    if (!filters.excludeTags.includes('NSFW')) {
      setFilters(prev => ({
        ...prev,
        excludeTags: [...prev.excludeTags, 'NSFW']
      }));
    }
  }, []);

  const handleShowHiddenChange = (event) => {
    setFilters(prev => ({ ...prev, showHidden: event.target.checked }));
  };

  const handleConfidenceRatingChange = (event, newValue) => {
    setFilters(prev => ({ ...prev, minScore: newValue[0], maxScore: newValue[1] }));
  };

  const handleAddTag = (event, value) => {
    if (event.key === 'Enter' && value && !filters.tags.includes(value)) {
      setFilters(prev => ({
        ...prev,
        tags: [...prev.tags, value]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddExcludeTag = (event, value) => {
    if (event.key === 'Enter' && value && !filters.excludeTags.includes(value)) {
      setFilters(prev => ({
        ...prev,
        excludeTags: [...prev.excludeTags, value]
      }));
      setExcludeTagInput('');
    }
  };

  const handleRemoveExcludeTag = (tagToRemove) => {
    setFilters(prev => ({
      ...prev,
      excludeTags: prev.excludeTags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      tags: [],
      excludeTags: ['NSFW'],
      minScore: 0,
      maxScore: 100,
      showHidden: false
    });
    setTagInput('');
    setExcludeTagInput('');
  };

  return (
    <Drawer 
      anchor="right" 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: { backgroundColor: theme.palette.background.default }
      }}
    >
      <Container sx={{ width: 300, pt: 2, pb: 4 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.showHidden}
              onChange={handleShowHiddenChange}
            />
          }
          label="Show Hidden Games"
        />

        <Divider sx={{ my: 2 }} />

        <Typography gutterBottom>Confidence Rating</Typography>
        <Slider
          value={[filters.minScore, filters.maxScore]}
          onChange={handleConfidenceRatingChange}
          valueLabelDisplay="auto"
          min={0}
          max={100}
          sx={{ color: theme.palette.primary.main }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="body2">Min: {filters.minScore}</Typography>
          <Typography variant="body2">Max: {filters.maxScore}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography gutterBottom>Tags</Typography>
        <Autocomplete
          freeSolo
          options={knownTags}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Type and press enter"
            />
          )}
          value={tagInput}
          onChange={(event, newValue) => setTagInput(newValue)}
          onKeyDown={(event) => handleAddTag(event, tagInput)}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {filters.tags.map(tag => (
            <Chip
              key={tag}
              label={tag}
              onDelete={() => handleRemoveTag(tag)}
              size="small"
            />
          ))}
        </Box>

        <Typography gutterBottom>Exclude Tags</Typography>
        <Autocomplete
          freeSolo
          options={knownTags}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Type and press enter"
            />
          )}
          value={excludeTagInput}
          onChange={(event, newValue) => setExcludeTagInput(newValue)}
          onKeyDown={(event) => handleAddExcludeTag(event, excludeTagInput)}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {filters.excludeTags.map(tag => (
            <Chip
              key={tag}
              label={tag}
              onDelete={() => handleRemoveExcludeTag(tag)}
              size="small"
            />
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="outlined" 
            onClick={handleClearFilters} 
            sx={{ color: theme.palette.primary.main, borderColor: theme.palette.primary.main }}
          >
            Clear Filters
          </Button>
        </Box>
      </Container>
    </Drawer>
  );
};

export default FilterDrawer;